import { MenuProps } from '@mui/material';
import { interpolateViridis } from 'd3-scale-chromatic';

import AnalyticsFilled from '@icons/AnalyticsFilled';
import AnalyticsOutlined from '@icons/AnalyticsOutlined';
import NewspaperFilled from '@icons/NewspaperFilled';
import NewspaperOutlined from '@icons/NewspaperOutlined';
import PeopleFilled from '@icons/PeopleFilled';
import PeopleOutlined from '@icons/PeopleOutlined';
import RadarFilled from '@icons/RadarFilled';
import RadarOutlined from '@icons/RadarOutlined';
import SenpaiOutline from '@icons/SenpaiOutline';
import TackleBoxFilled from '@icons/TackleBoxFilled';
import TackleBoxOutlined from '@icons/TackleBoxOutlined';
import TouchstoneFilled from '@icons/TouchstoneFilled';
import TouchstoneOutlined from '@icons/TouchstoneOutlined';
import WifiFilled from '@icons/WifiFilled';
import WifiOutlined from '@icons/WifiOutlined';
import { Basemap } from '@types';
import { scaleLinear } from 'd3-scale';
import { NavbarOptions } from './types/navbar';
import { hexToRgb } from './utils';
const { VITE_APP_VERSION } = import.meta.env;
const { VITE_MAPBOX_ACCESS_TOKEN } = import.meta.env;

export const APP_VERSION = VITE_APP_VERSION || 'development';

export const ROUTES = {
  HOME: '/',
  TITO: '/tito',
  ADMIN: '/admin',
  ADMIN_USERS: '/admin/users',
  STATS: '/stats'
} as const;

export const AUTH_FALLBACK = {
  loginWithRedirect: () => null,
  getAccessTokenSilently: async () => '',
  user: {},
  error: null,
  isLoading: false,
  logout: () => undefined,
  isAuthenticated: true
};

export const MAPBOX_ACCESS_TOKEN = VITE_MAPBOX_ACCESS_TOKEN;

export const BASEMAPS: Basemap[] = [
  {
    id: 'mapbox_light_v11',
    name: 'Light',
    url: 'mapbox://styles/mapbox/light-v11',
    type: 'style',
    layers: [],
    image: '/map_layers/mapbox_light.webp',
    theme: 'light'
  },
  {
    id: 'mapbox_dark_v11',
    name: 'Dark',
    url: 'mapbox://styles/mapbox/dark-v11',
    type: 'style',
    layers: [],
    image: '/map_layers/mapbox_dark.webp',
    theme: 'dark'
  },
  {
    id: 'mapbox_streets_v12',
    name: 'Streets',
    url: 'mapbox://styles/mapbox/streets-v12',
    type: 'style',
    layers: [],
    image: '/map_layers/mapbox_streets.webp',
    theme: 'light'
  },
  {
    id: 'mapbox_outdoors_v12',
    name: 'Outdoors',
    url: 'mapbox://styles/mapbox/outdoors-v12',
    type: 'style',
    layers: [],
    image: '/map_layers/mapbox_outdoors.webp',
    theme: 'light'
  },
  {
    id: 'mapbox_navigation_day_v1',
    name: 'Nav day',
    url: 'mapbox://styles/mapbox/navigation-day-v1',
    type: 'style',
    layers: [],
    image: '/map_layers/mapbox_nav_day.webp',
    theme: 'light'
  },
  {
    id: 'mapbox_navigation_night_v1',
    name: 'Nav night',
    url: 'mapbox://styles/mapbox/navigation-night-v1',
    type: 'style',
    layers: [],
    image: '/map_layers/mapbox_nav_night.webp',
    theme: 'dark'
  },
  {
    id: 'mapbox_satellite_v9',
    name: 'Satellite',
    url: 'mapbox://styles/mapbox/satellite-v9',
    type: 'style',
    layers: [],
    image: '/map_layers/mapbox_satellite.webp',
    theme: 'dark'
  },
  {
    id: 'osm',
    name: 'OSM',
    url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    type: 'tilelayer',
    layers: [],
    image: '/map_layers/osm.webp',
    theme: 'light'
  },
  {
    id: 'google_satellite',
    name: 'Google sat',
    url: 'https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
    type: 'tilelayer' as const,
    layers: [],
    image: '/map_layers/google_satellite.webp',
    theme: 'dark'
  }
];

// High saturation basemaps that need a semi-transparent overlay for better contrast
export const HIGH_SATURATION_BASEMAPS = ['mapbox_satellite_v9', 'osm', 'google_satellite'] as const;

export const WIFI_SCORE_OPTIONS = {
  low: { value: 'low', labelLong: 'Low', labelShort: 'Low', color: '#ef6c00' },
  medium: { value: 'medium', labelLong: 'Medium', labelShort: 'Med', color: '#b29c5b' },
  high: { value: 'high', labelLong: 'High', labelShort: 'High', color: '#2e7d32' }
} as const;

export const MENU_ITEM_MIN_WIDTH = 200;
export const MAP_PANEL_MAX_HEIGHT = 350;
export const MAP_TRANSITION_ZOOM = 8;
export const MAX_ROWS = 10_001;
export const MAX_ARI_BURSTS = 1000;
export const MAX_DATAMINR_ALERTS = 500; // MAX_LIMIT in api-dataminr
export const REFETCH_THRESHOLD = 0.9;
export const EGE_LABEL = 'EGE';
export const LEADS_LABEL = 'AdTech'; // sync changes to misc.ts
export const APP_NAME = 'SEnPAI';
export const EGE_H3_ZOOM_THRESHOLD = 6.5;
export const MAX_MANUAL_EGE_FETCH_KM2 = 15; // 15 km²

export const NOTIFICATION_TYPE_TITLE = {
  MOTD: 'Message of the Day',
  JOB_UPDATE: 'Job Update',
  ALERT: 'Alert',
  TACKLEBOX: 'Tacklebox',
  CAMPAIGN: 'Campaign'
};

export const MENU_PROPS_OPEN_TO_LEFT: Partial<Omit<MenuProps, 'children'>> = {
  anchorOrigin: {
    vertical: 'top',
    horizontal: 'left'
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'right'
  }
};

export const ButtonKey = {
  polygon: 'polygon',
  ege: 'ege',
  wifi: 'wifi',
  cell: 'cell',
  foundri: 'foundri',
  midb: 'midb',
  hexagg: 'hexagg',
  senpai: 'senpai',
  adsb: 'adsb',
  ais: 'ais'
} as const;

export type ButtonKeyType = keyof typeof ButtonKey;

export const BUTTON_COLORS: Record<ButtonKeyType, { active: string; hover: string }> = {
  polygon: { active: '#707070', hover: '#BDBDBD' },
  ege: { active: '#707070', hover: '#BDBDBD' },
  hexagg: { active: '#707070', hover: '#BDBDBD' },
  wifi: { active: '#4DB6AC', hover: '#B2DFDB' },
  senpai: { active: '#BA68C8', hover: '#E1BEE7' },
  cell: { active: '#6E91C7', hover: '#B0C4DE' },
  foundri: { active: '#8BC34A', hover: '#C5E1A5' },
  midb: { active: '#BA68C8', hover: '#E1BEE7' },
  adsb: { active: '#AFB42B', hover: '#D4E157' },
  ais: { active: '#03A9F4', hover: '#81D4FA' }
};

/** The one colour Dataminr is drawn in: map points, cards and the navbar. */
export const DATAMINR_COLOR = '#BF360C';

/**
 * The theme's base face is Open Sans; the Dataminr designs specify DM Sans.
 * MUI writes the theme family onto every Typography, so a parent cannot cascade
 * a different one — each element opts in with this.
 */
export const DM_SANS_FONT_FAMILY = '"DM Sans Variable"';

export const BUTTON_TOOLTIP: Record<ButtonKeyType, { on: string; off: string; campaign?: string; comprehensive?: string }> = {
  polygon: {
    on: 'Polygon layer visible',
    off: 'Polygon layer hidden',
    campaign: 'Campaign polygons only',
    comprehensive: `Polygon layer:
    Click to cycle through:
    • Campaign Polygons (if campaign active)
    • All Polygons
    • Off
    `
  },
  ege: { on: 'Collateral Telemetry Data (EGE) layer visible', off: 'Collateral Telemetry Data (EGE) layer hidden' },
  wifi: { on: 'Techno Dance Party (WIFI) layer visible', off: 'Techno Dance Party (WIFI) layer hidden' },
  cell: { on: 'Cell layer visible', off: 'Cell layer hidden' },
  foundri: { on: 'FounDRI layer visible', off: 'FounDRI layer hidden' },
  midb: { on: 'Modernized Integrated Database (MIDB) layer visible', off: 'Modernized Integrated Database (MIDB) layer hidden' },
  hexagg: { on: 'Hex Agg layer visible', off: 'Hex Agg layer hidden' },
  senpai: { on: 'RF/Senpai layer visible', off: 'RF/Senpai layer hidden' },
  adsb: { on: 'ADSB flights visible', off: 'ADSB flights hidden' },
  ais: { on: 'AIS vessels visible', off: 'AIS vessels hidden' }
};

export const RESET_VIEW_CONFIG = {
  displayName: 'SEnPAI',
  inactiveIcon: SenpaiOutline,
  activeIcon: SenpaiOutline,
  group: 'main' as const,
  enabled: true,
  isToggle: true,
  tooltip: 'Reset map: clear layers, fit global view, close panel'
};

export const NAVBAR_VIEWS: NavbarOptions = {
  touchstone: {
    // \n
    displayName: 'AdTech',
    inactiveIcon: TouchstoneOutlined,
    activeIcon: TouchstoneFilled,
    product: 'touchstone',
    group: 'main',
    enabled: true,
    hasTab: true,
    isToggle: false,
    showInNavbar: false,
    activeColor: BUTTON_COLORS.polygon.active,
    toggleAction: 'toggleTouchstoneData',
    tooltipActive: 'Click to hide the AdTech layer on the map.',
    tooltipInactive: 'Click to show the AdTech layer on the map.'
  },
  wifi: {
    displayName: 'WiFi',
    inactiveIcon: WifiOutlined,
    activeIcon: WifiFilled,
    product: 'wifi',
    group: 'main',
    enabled: true,
    hasTab: true,
    isToggle: true,
    showInNavbar: true,
    activeColor: BUTTON_COLORS.wifi.active,
    toggleAction: 'toggleWIFIData',
    tooltipActive: 'Click to hide the WiFi layer on the map.',
    tooltipInactive: 'Click to show the WiFi layer on the map.'
  },
  ari: {
    displayName: 'RF',
    inactiveIcon: RadarOutlined,
    activeIcon: RadarFilled,
    product: 'ari',
    group: 'main',
    enabled: true,
    hasTab: true,
    isToggle: true,
    showInNavbar: true,
    activeColor: BUTTON_COLORS.senpai.active,
    toggleAction: 'toggleARIData',
    tooltipActive: 'Click to hide the RF layer on the map.',
    tooltipInactive: 'Click to show the RF layer on the map.'
  },
  dataminr: {
    displayName: 'News',
    inactiveIcon: NewspaperOutlined,
    activeIcon: NewspaperFilled,
    product: 'dataminr',
    group: 'main',
    enabled: true,
    hasTab: true,
    isToggle: true,
    showInNavbar: true,
    activeColor: DATAMINR_COLOR,
    toggleAction: 'toggleDataminrData',
    tooltipActive: 'Click to stop fetching news alerts.',
    tooltipInactive: 'Click to fetch news alerts for the current map view.'
  },
  // foundri: {
  //   displayName: 'Foundri',
  //   inactiveIcon: MapPinOutlined,
  //   activeIcon: MapPinFilled,
  //   path: '/foundri',
  //   group: 'main',
  //   enabled: true
  // },
  // midb: {
  //   displayName: 'MIDB',
  //   inactiveIcon: GlobeOutlined,
  //   activeIcon: GlobeFilled,
  //   path: '/midb',
  //   group: 'main',
  //   enabled: true
  // },

  tito: {
    displayName: 'TiTo',
    inactiveIcon: PeopleOutlined,
    activeIcon: PeopleFilled,
    product: 'tito',
    group: 'main',
    enabled: true,
    hasTab: true,
    isToggle: false,
    showInNavbar: false
  },

  stats: {
    displayName: 'Stats',
    inactiveIcon: AnalyticsOutlined,
    activeIcon: AnalyticsFilled,
    path: ROUTES.STATS,
    group: 'main',
    enabled: true,
    // A PAGE, not a map layer. Every other 'main' entry toggles something on the
    // touchstone map; this one navigates, which is what `path` with isToggle false
    // means to Navbar.onViewChange.
    isToggle: false,
    hasTab: false,
    // Excluded from the mapped group and rendered explicitly by Navbar, after
    // Tacklebox and its own divider -- the same treatment Touchstone and Tacklebox get.
    // Position in this object no longer decides where it appears.
    showInNavbar: false,
    tooltip: 'EGE statistics by country and date.'
  },
  tacklebox: {
    displayName: 'Tacklebox',
    inactiveIcon: TackleBoxOutlined,
    activeIcon: TackleBoxFilled,
    group: 'main',
    enabled: true,
    hasTab: false,
    hasPopover: true,
    showInNavbar: false,
    popoverOptions: [
      {
        label: 'TiTo',
        toggleAction: 'toggleTitoData',
        ActiveIcon: PeopleFilled,
        InactiveIcon: PeopleOutlined
      }
    ]
  }

  // foundri: {
  //   displayName: 'Foundri',
  //   inactiveIcon: MapPinOutlined,
  //   activeIcon: MapPinFilled,
  //   path: '/foundri',
  //   group: 'main',
  //   enabled: true,
  //   isToggle: true,
  //   toggleAction: 'toggleFoundriData'
  // },
  // midb: {
  //   displayName: 'MIDB',
  //   inactiveIcon: GlobeOutlined,
  //   activeIcon: GlobeFilled,
  //   path: '/midb',
  //   group: 'main',
  //   enabled: true,
  //   isToggle: true,
  //   toggleAction: 'toggleMIDBData'
  // },
};

interface TopicColor {
  light: string;
  dark: string;
  mapPolygonFillColor: [number, number, number, number];
  mapPolygonBorderColorSelected: [number, number, number, number];
}
export const TOPIC_COLORS: Record<string, TopicColor> = {
  airfield: {
    light: '#a5a6b6',
    dark: '#8664c4',
    mapPolygonFillColor: hexToRgb('#8664c4', 120),
    mapPolygonBorderColorSelected: hexToRgb('#8664c4', 255)
  },
  military: {
    light: '#FFD5D5',
    dark: '#FF8080',
    mapPolygonFillColor: hexToRgb('#FF8080', 120),
    mapPolygonBorderColorSelected: hexToRgb('#FF8080', 255)
  },
  diplomatic: {
    light: '#b3bfdd',
    dark: '#4963a1',
    mapPolygonFillColor: hexToRgb('#4963a1', 120),
    mapPolygonBorderColorSelected: hexToRgb('#4963a1', 255)
  },
  'defense industry': {
    light: '#FFE0B2',
    dark: '#EF6C00',
    mapPolygonFillColor: hexToRgb('#EF6C00', 120),
    mapPolygonBorderColorSelected: hexToRgb('#EF6C00', 255)
  },
  university: {
    dark: '#D9D0C9',
    light: '#F2EFE9',
    mapPolygonFillColor: hexToRgb('#D9D0C9', 120),
    mapPolygonBorderColorSelected: hexToRgb('#D9D0C9', 255)
  }
};
export const NULL_TOPIC_COLOR: TopicColor = {
  light: '#E0E0E0',
  dark: '#0000008f',
  mapPolygonFillColor: hexToRgb('#ddd', 120),
  mapPolygonBorderColorSelected: hexToRgb('#ddd', 255)
};
export type Topic = keyof typeof TOPIC_COLORS;
export const TOPICS: Topic[] = Object.keys(TOPIC_COLORS);

// Matches the service's default categories for GET /v0/location
export const DEFAULT_LOCATION_FILTER_CATEGORIES = ['military', 'diplomatic', 'airfield', 'defense industry'];

export const TOPIC_COLOR_UNKNOWN = '#FFFF00';
export const TOPIC_COLOR_UNKNOWN_RGB = [255, 255, 0, 255] as [number, number, number, number];
export const MAX_ANIMATED_ZOOM = 13.5;
export const CENTROID_TO_BOUNDING_BOX_OFFSET = 0.005;
export const WIFI_CARD_COLORS = {
  selected: '#00897B99',
  detailView: '#00897B99'
};

const WIFI_OBSERVATION_HEX = {
  connected: '#f8b55f',
  surveyed: '#ef4444'
} as const;

export const WIFI_OBSERVATION_COLORS = {
  connected: {
    hex: WIFI_OBSERVATION_HEX.connected,
    rgb: hexToRgb(WIFI_OBSERVATION_HEX.connected, 255)
  },
  surveyed: {
    hex: WIFI_OBSERVATION_HEX.surveyed,
    rgb: hexToRgb(WIFI_OBSERVATION_HEX.surveyed, 255)
  }
} as const;

export const ARI_CARD_COLORS = {
  selected: '#BA68C8B3',
  detailView: '#BA68C8B3'
};

export const DATAMINR_CARD_COLORS = {
  selected: `${DATAMINR_COLOR}B3`,
  detailView: `${DATAMINR_COLOR}B3`
};

export const H3_RAW_SCALE_COLORS_CSS: [number, number, number, number][] = [
  // [247, 247, 247, 100],
  [204, 204, 204, 0.39],
  [164, 164, 164, 0.39],
  [124, 124, 124, 0.39],
  [84, 84, 84, 0.39],
  [0, 0, 0, 0.39]
];
export const H3_SCALE_COLORS_DECKGL: [number, number, number, number][] = [
  // [247, 247, 247, 100],
  [204, 204, 204, 100],
  [164, 164, 164, 100],
  [124, 124, 124, 100],
  [84, 84, 84, 100],
  [0, 0, 0, 100]
];

export const H3_CLUSTER_SCALE_COLORS_DECKGL: [number, number, number, number][] = [
  [173, 216, 230, 100], // Light blue
  [135, 206, 250, 100], // Sky blue
  [70, 130, 180, 100], // Steel blue
  [30, 144, 255, 100], // Dodger blue
  [0, 0, 139, 100] // Dark blue
];

export const VIRIDIS_SCALE_COLORS_DECKGL = [
  // \n
  interpolateViridis(1),
  interpolateViridis(0.75),
  interpolateViridis(0.5),
  interpolateViridis(0.25),
  interpolateViridis(0)
].map((color) => hexToRgb(color, 100));

export const VIRIDIS_SCALE_COLORS_CSS = [
  // \n
  interpolateViridis(1),
  interpolateViridis(0.75),
  interpolateViridis(0.5),
  interpolateViridis(0.25),
  interpolateViridis(0)
].map((color) => hexToRgb(color, 0.39));

export const PANEL_WIDTH = 294;
export const POLYGON_CARD_HEIGHT = 126;
export const POLYGON_CARD_WIDTH = 133;
export const ENTITY_CARD_HEIGHT = 60;
export const DETAIL_PANEL_HEIGHT = 400;

const SCATTERPLOT_SCALE = scaleLinear() // n
  .domain([0, 7, 10, 12, 15, 20, 22])
  .range([0.15, 0.065, 0.1, 0.08, 0.1, 0.2, 0.5])
  .clamp(true);
export const getScatterplotScale = (zoom: number = 17) => {
  return SCATTERPLOT_SCALE(zoom);
};

export const DEFAULT_SCATTERPLOT_PROPS = {
  filled: true,
  lineWidthMinPixels: 1,
  opacity: 0.8,
  pickable: true,
  radiusMaxPixels: 10,
  radiusMinPixels: 1,
  radiusScale: 20,
  stroked: false,
  wrapLongitude: true,
  radiusUnits: 'pixels' as const,
  lineWidthUnits: 'pixels' as const
};

export const UTC_DATE_TIME_FORMAT = 'YYYY/MM/DD HH:mm[Z]';
export const UTC_DATE_FORMAT = 'YYYY/MM/DD';

// protobuf encodes coordinates as integers multiplied by 10 million
export const COORDINATE_CONVERSION_FACTOR = 10_000_000;

export enum TackleboxTools {
  TITO = 'tito'
}

// mapping for tacklebox tool names
export const TackleboxToolDisplayInfo = {
  [TackleboxTools.TITO]: { displayName: 'TiTo', description: 'Tell me identifiers common in Two or more polygons' }
};

// kilometer squared
export const MAX_POLYGONS_AREA = 1_000_000;

export const METER_TO_FT = 3.28084;

export const AUDIENCE_SELECT_TOP_N = 100;
